/**
 * Tårnforsvar maps. Every map is a 14 × 9 grid with a winding route of axis-aligned segments (corner points in cell coordinates).
 * The route always starts on the LEFT edge and ends on the RIGHT edge so the 3D camera, the picking and the 2D fallback work for
 * every map; what differs is the shape of the route, the scenery (see src/client/games/td3d/themes.ts) and the tuning.
 * Pure data + geometry – shared by the server (validation, simulation) and the client (rendering).
 */
export const COLS = 14;
export const ROWS = 9;

export type MapId = "skov" | "oerken" | "vinter" | "vulkan" | "slik";
export const MAP_IDS: MapId[] = ["skov", "oerken", "vinter", "vulkan", "slik"];
export const DEFAULT_MAP: MapId = "skov";
export const isMapId = (v: unknown): v is MapId => typeof v === "string" && (MAP_IDS as string[]).includes(v);

export interface MapDef {
  id: MapId;
  name: string;
  tagline: string;
  /** what the balloons come out of / what they are trying to reach (Danish, used in the UI) */
  start: string;
  goal: string;
  waypoints: [number, number][];
  /** 1–3 stars: how demanding the layout is (a hint in the picker – the real difficulty is chosen separately) */
  stars: 1 | 2 | 3;
  /** small fine-tuning of the number of balloons so that every layout feels equally fair (1 = unchanged) */
  countMul: number;
  /** colours for the 2D preview and the flat fallback view */
  palette: { sky: string; sky2: string; ground: string; ground2: string; road: string; road2: string; accent: string; ink: string };
}

export const MAP_DEFS: Record<MapId, MapDef> = {
  skov: {
    id: "skov",
    name: "Skovstien",
    tagline: "Den klassiske bane – fire lange sving gennem den grønne skov.",
    start: "Hulen",
    goal: "Slottet",
    waypoints: [[0, 1], [11, 1], [11, 3], [2, 3], [2, 5], [11, 5], [11, 7], [13, 7]],
    stars: 1,
    countMul: 1,
    palette: { sky: "#9fd8ff", sky2: "#e8f6ff", ground: "#6dbb3c", ground2: "#4d9a2e", road: "#c8b48a", road2: "#a8946a", accent: "#ffcf4a", ink: "#274e13" },
  },
  oerken: {
    id: "oerken",
    name: "Ørkenoasen",
    tagline: "Pyramider, palmer og en skjult oase. Lodrette sving i det varme sand.",
    start: "Pyramiden",
    goal: "Guldtemplet",
    waypoints: [[0, 6], [2, 6], [2, 1], [5, 1], [5, 7], [8, 7], [8, 1], [11, 1], [11, 6], [13, 6]],
    stars: 2,
    countMul: 0.98,
    palette: { sky: "#ffd9a0", sky2: "#fff1d0", ground: "#e9c27a", ground2: "#d4a45a", road: "#b97b4a", road2: "#8f5a33", accent: "#2bb7a6", ink: "#6a3d15" },
  },
  vinter: {
    id: "vinter",
    name: "Vinterlandet",
    tagline: "Sne, is og nordlys. Vejen snor sig i en stor spiral.",
    start: "Isgrotten",
    goal: "Isslottet",
    waypoints: [[0, 7], [5, 7], [5, 3], [1, 3], [1, 1], [8, 1], [8, 5], [11, 5], [11, 2], [13, 2]],
    stars: 2,
    countMul: 1.02,
    palette: { sky: "#6ea8ff", sky2: "#d8ecff", ground: "#eaf3fb", ground2: "#c8dcec", road: "#9fb6cc", road2: "#7c97b0", accent: "#7ee8ff", ink: "#1d3b63" },
  },
  vulkan: {
    id: "vulkan",
    name: "Vulkanøen",
    tagline: "Glødende lava og sort sten. Vejen klatrer op ad vulkanen og snor sig ned igen.",
    start: "Krateret",
    goal: "Tikitemplet",
    waypoints: [[0, 7], [3, 7], [3, 5], [6, 5], [6, 3], [9, 3], [9, 1], [11, 1], [11, 5], [8, 5], [8, 7], [13, 7]],
    stars: 3,
    countMul: 1.04,
    palette: { sky: "#ff9a5a", sky2: "#ffd2a0", ground: "#3a3340", ground2: "#26212d", road: "#6d5a52", road2: "#463a36", accent: "#ff5a1f", ink: "#ffd9a0" },
  },
  slik: {
    id: "slik",
    name: "Slikland",
    tagline: "Lollipops, chokoladefloder og cupcake-slot. Sødt – men balloner er ikke søde!",
    start: "Kagehuset",
    goal: "Cupcake-slottet",
    waypoints: [[0, 1], [6, 1], [6, 4], [1, 4], [1, 7], [9, 7], [9, 3], [13, 3]],
    stars: 1,
    countMul: 1,
    palette: { sky: "#ffc4ec", sky2: "#fff0fa", ground: "#ffb8dc", ground2: "#ff94c8", road: "#fff4b8", road2: "#ffd36e", accent: "#38d6c4", ink: "#a2306e" },
  },
};

/** A map with its derived geometry: all path cells and positions along the route. */
export class TdMap {
  readonly id: MapId;
  readonly cells: [number, number][] = [];
  readonly length: number;
  private readonly set = new Set<string>();
  private readonly seg: number[] = [];

  constructor(readonly def: MapDef) {
    this.id = def.id;
    const wp = def.waypoints;
    let total = 0;
    for (let i = 0; i < wp.length - 1; i++) {
      const [ax, ay] = wp[i]!;
      const [bx, by] = wp[i + 1]!;
      const dx = Math.sign(bx - ax);
      const dy = Math.sign(by - ay);
      let x = ax, y = ay;
      for (let guard = 0; guard < 4 * (COLS + ROWS); guard++) {
        const k = `${x},${y}`;
        if (!this.set.has(k)) { this.set.add(k); this.cells.push([x, y]); }
        if (x === bx && y === by) break;
        x += dx;
        y += dy;
      }
      const l = Math.abs(bx - ax) + Math.abs(by - ay);
      this.seg.push(l);
      total += l;
    }
    this.length = total;
  }

  get waypoints() { return this.def.waypoints; }
  isPath(x: number, y: number) { return this.set.has(`${x},${y}`); }

  /** Position (in cell units, cell centre = +0.5) of a balloon that has travelled `d` cells along the route. */
  posAt(d: number): [number, number] {
    const wp = this.def.waypoints;
    let rest = Math.max(0, Math.min(this.length, d));
    for (let i = 0; i < this.seg.length; i++) {
      const l = this.seg[i]!;
      if (rest <= l || i === this.seg.length - 1) {
        const [ax, ay] = wp[i]!;
        const [bx, by] = wp[i + 1]!;
        const f = l === 0 ? 0 : rest / l;
        return [ax + (bx - ax) * f + 0.5, ay + (by - ay) * f + 0.5];
      }
      rest -= l;
    }
    const last = wp[wp.length - 1]!;
    return [last[0] + 0.5, last[1] + 0.5];
  }
}

export const MAPS: Record<MapId, TdMap> = Object.fromEntries(MAP_IDS.map((id) => [id, new TdMap(MAP_DEFS[id])])) as Record<MapId, TdMap>;
export const getMap = (id: unknown): TdMap => (isMapId(id) ? MAPS[id] : MAPS[DEFAULT_MAP]);

/** Problems with a map layout (empty = fine). Used by the unit tests so that a new map can never break the game. */
export function validateMap(def: MapDef): string[] {
  const out: string[] = [];
  const wp = def.waypoints;
  if (wp.length < 3) out.push("for få hjørner");
  if (wp[0]![0] !== 0) out.push("starten skal ligge i venstre side");
  if (wp[wp.length - 1]![0] !== COLS - 1) out.push("målet skal ligge i højre side");
  if (wp[1]![1] !== wp[0]![1]) out.push("første stykke skal være vandret");
  if (wp[wp.length - 1]![1] !== wp[wp.length - 2]![1]) out.push("sidste stykke skal være vandret");
  const seen = new Map<string, number>();
  for (let i = 0; i < wp.length; i++) {
    const [x, y] = wp[i]!;
    if (x < 0 || y < 0 || x >= COLS || y >= ROWS) out.push(`hjørne ${i} ligger uden for banen`);
    if (i > 0) {
      const [px, py] = wp[i - 1]!;
      if (px !== x && py !== y) out.push(`stykke ${i} er skråt`);
      if (px === x && py === y) out.push(`stykke ${i} har længde 0`);
      if (i > 1) {
        const [qx, qy] = wp[i - 2]!;
        const turn = (qx === px) !== (px === x);
        if (!turn) out.push(`hjørne ${i - 1} er ikke et sving`);
      }
    }
  }
  // the geometry below only makes sense for a well-formed route (axis-aligned, inside the grid)
  if (out.length) return out;
  const m = new TdMap(def);
  let n = 0;
  for (let i = 0; i < wp.length - 1; i++) {
    const [ax, ay] = wp[i]!;
    const [bx, by] = wp[i + 1]!;
    const dx = Math.sign(bx - ax), dy = Math.sign(by - ay);
    let x = ax, y = ay;
    for (;;) {
      const k = `${x},${y}`;
      const at = seen.get(k);
      // a cell may only be visited again as the corner shared by two consecutive segments
      if (at !== undefined && at !== i - 1 && !(at === i)) out.push(`ruten krydser sig selv i (${x},${y})`);
      if (at === undefined) seen.set(k, i);
      n++;
      if (x === bx && y === by) break;
      x += dx;
      y += dy;
    }
  }
  if (m.length < 26 || m.length > 46) out.push(`ruten er ${m.length} felter lang (ønsket 26–46)`);
  // two parallel stretches need at least one buildable cell between them
  for (let i = 0; i < m.cells.length; i++) {
    const [x, y] = m.cells[i]!;
    for (let j = i + 1; j < m.cells.length; j++) {
      const [x2, y2] = m.cells[j]!;
      if (Math.abs(x - x2) + Math.abs(y - y2) === 1 && !isNeighbourInRoute(m, i, j)) out.push(`ruten ligger klistret til sig selv ved (${x},${y})`);
    }
  }
  const free = COLS * ROWS - m.cells.length;
  if (free < 60) out.push("for få byggefelter");
  void n;
  return out;
}
/** two cells that touch are fine when they are consecutive along the route */
function isNeighbourInRoute(m: TdMap, i: number, j: number) {
  return j === i + 1;
}
